import { FunctionComponent } from 'react';
import { Spinner } from 'components/UI';
import styles from './MonthlyNetSalesTable.module.scss';

export interface MonthlyNetSalesRow {
    category: string;
    monthly: number[]; // 12 values Jan..Dec
    total: number;
    percentage: number; // e.g. 0.398 => 39.8%
}

interface Props {
    rows: MonthlyNetSalesRow[];
    currency?: string;
    year?: number;
    loading?: boolean;
}

const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

const formatCurrency = (v: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 3
    }).format(v);
};

export const MonthlyNetSalesTable: FunctionComponent<Props> = ({ rows, currency = 'USD', year, loading }) => {
    return (
        <div className={styles.root}>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead className={styles.header}>
                        <tr>
                            <th className={styles.headerCell}>Category</th>
                            {monthNames.map(m => (
                                <th
                                    key={m}
                                    className={styles.headerCell}
                                >
                                    {m}
                                </th>
                            ))}
                            <th className={styles.headerCell}>{year ?? ''}</th>
                            <th className={styles.headerCell}>% of Total</th>
                        </tr>
                    </thead>
                    <tbody className={styles.body}>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={15}
                                    className={styles.loadingCell}
                                >
                                    <Spinner className={styles.spinner} />
                                </td>
                            </tr>
                        ) : (
                            rows.map(r => {
                                const isTotal = r.category.toLowerCase().includes('total');
                                return (
                                    <tr
                                        key={r.category}
                                        className={isTotal ? styles.totalRow : styles.row}
                                    >
                                        <td className={styles.cell}>{r.category}</td>
                                        {r.monthly.map((v, i) => (
                                            <td
                                                key={i}
                                                className={styles.cell}
                                            >
                                                {formatCurrency(v, currency)}
                                            </td>
                                        ))}
                                        <td className={styles.cell}>{formatCurrency(r.total, currency)}</td>
                                        <td className={styles.cell}>{(r.percentage * 100).toFixed(3)}%</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MonthlyNetSalesTable;
